/**
 * Auditor Dashboard — read-only, audit-chain-led home.
 *
 * Design intent: this persona is an internal or external auditor whose
 * primary unit of work is **the hash-chained audit log + evidence
 * provenance**. They need to answer in 60 seconds: is the chain intact,
 * which frameworks are at risk, what evidence is available, and what
 * material events have happened recently. Everything is read-only; the
 * UI mirrors the auditor's mental model (provenance → posture → events
 * → evidence) rather than the operator's (agents → cost → vendors).
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  FileCheck2,
  History,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ScrollText,
  ChevronRight,
  GitBranch,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import {
  fmtAud,
  KpiTile,
  PanelCard,
  SectionEyebrow,
} from "./components/DashboardPrimitives";
import { cn } from "@/lib/utils";

export default function AuditorDashboard() {
  return (
    <DashboardDataProvider>
      <AuditorInner />
    </DashboardDataProvider>
  );
}

function AuditorInner() {
  const {
    tenant,
    kpis,
    frameworks,
    reports,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useDashboardData();
  const { auditEntries } = useTenantData();

  const recent = useMemo(() => (auditEntries || []).slice(0, 12), [auditEntries]);
  const chainHead = recent[0]?.hash || "—";
  const chainLength = (auditEntries || []).length;
  const incidentsOpen = kpis?.incidentsOpen ?? 0;
  const policyViolations = kpis?.policyViolations30d ?? 0;
  const signedReports = (reports || []).filter((r) => r.signed).length;
  const totalReports = (reports || []).length;

  if (loading && !tenant) return <AuditorSkeleton />;
  if (error) return <AuditorError message={error.message} />;

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="auditor-dashboard">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.04} />
      </div>

      {/* Header — auditor-flavoured ceremonial intro */}
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Assurance · Auditor View</p>
            <div className="flex items-center gap-2 mt-1">
              <h1 className="text-display-md text-ink-primary">{tenant?.name}</h1>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-inset border border-border-strong text-[10px] font-bold tracking-[0.08em] uppercase text-ink-secondary"
                data-testid="auditor-readonly-badge"
                title="Auditor seat. Read-only — mutations are blocked at the API."
              >
                <Lock size={10} /> Read-only
              </span>
            </div>
            <p className="font-ceremonial italic text-ink-secondary text-[17px] mt-1 max-w-2xl">
              Every claim above traceable to a hash-chained source. Verify, do not trust.
            </p>
            <p className="text-meta text-ink-disabled mt-2">
              {tenant?.industry} · {tenant?.region} · view as of {new Date().toISOString().slice(0, 10)}
            </p>
          </div>
          <SyncedIndicator
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefetch={refetch}
            testId="auditor-synced"
          />
        </div>
      </div>

      {/* KPI strip — chain + frameworks + signed + open findings */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile
          label="Audit chain"
          value={`${chainLength}`}
          delta="hash-chained"
          deltaTone="up"
          hint={`head ${String(chainHead).slice(0, 10)}…`}
          icon={GitBranch}
          tone="tralenex"
          testId="auditor-kpi-chain"
        />
        <KpiTile
          label="Framework coverage"
          value={`${kpis?.avgComplianceCoverage ?? 0}%`}
          delta={`${(frameworks || []).length} mapped`}
          deltaTone="neutral"
          hint="weighted across all controls"
          icon={ShieldCheck}
          tone="gold"
          testId="auditor-kpi-frameworks"
        />
        <KpiTile
          label="Signed evidence"
          value={`${signedReports} / ${totalReports}`}
          delta="tamper-evident"
          deltaTone="up"
          hint="every pack hash-stamped"
          icon={FileCheck2}
          tone="opp"
          testId="auditor-kpi-signed"
        />
        <KpiTile
          label="Open findings"
          value={`${incidentsOpen + policyViolations}`}
          delta={`${policyViolations} pol · ${incidentsOpen} inc`}
          deltaTone={incidentsOpen + policyViolations === 0 ? "up" : "down"}
          hint="policy + incident · last 30d"
          icon={AlertTriangle}
          tone={incidentsOpen + policyViolations === 0 ? "low" : "high"}
          testId="auditor-kpi-findings"
        />
      </div>

      {/* Body — chain integrity card (full width hero) */}
      <div className="mt-6">
        <ChainIntegrityCard chainHead={chainHead} chainLength={chainLength} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <FrameworkPostureCard frameworks={frameworks || []} />
          <RecentMaterialEventsCard entries={recent} />
        </div>
        <div className="flex flex-col gap-4">
          <SignedEvidencePackCard reports={reports || []} />
          <AuditorWorkflowCard
            chainLength={chainLength}
            incidentsOpen={incidentsOpen}
            policyViolations={policyViolations}
            kpis={kpis}
          />
        </div>
      </div>
    </div>
  );
}

function ChainIntegrityCard({ chainHead, chainLength }) {
  return (
    <PanelCard
      title="Audit chain · integrity"
      action={
        <Link
          to="/settings/audit"
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-800 inline-flex items-center gap-0.5"
          data-testid="auditor-open-chain"
        >
          Open chain <ChevronRight size={11} />
        </Link>
      }
      testId="auditor-chain-card"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-risk-low/10 border border-risk-low/30 text-risk-low font-semibold">
          <CheckCircle2 size={14} /> Verified · genesis → head
        </span>
        <div className="flex flex-col">
          <span className="text-meta text-ink-disabled uppercase tracking-[0.06em]">Entries</span>
          <span className="text-h2 text-ink-primary tabular-nums">{chainLength}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-meta text-ink-disabled uppercase tracking-[0.06em]">Head hash</span>
          <span className="font-mono text-sm text-ink-primary tabular-nums tracking-tight">
            {String(chainHead).slice(0, 16)}…
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-meta text-ink-disabled uppercase tracking-[0.06em]">Algorithm</span>
          <span className="text-sm text-ink-primary font-semibold">SHA-256 · prevHash linked</span>
        </div>
        <span className="ml-auto text-meta text-ink-secondary max-w-[280px] text-right">
          Re-verifiable offline by any external party — no trust in ALGOS required.
        </span>
      </div>
    </PanelCard>
  );
}

function FrameworkPostureCard({ frameworks }) {
  // Sort by coverage so the lowest is shown first (auditor's biggest worry).
  const sorted = [...frameworks].sort((a, b) => (a.coverage || 0) - (b.coverage || 0));
  return (
    <PanelCard title="Framework posture" testId="auditor-frameworks-card">
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {sorted.map((f) => {
          const cov = f.coverage ?? 0;
          const tone = postureTone(cov);
          return (
            <li
              key={f.id || f.name}
              className="surface-card-inner p-3 border border-border-subtle rounded-md"
              data-testid={`auditor-fw-${f.id || f.name}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-bold text-ink-primary truncate">{f.name}</p>
                <span className={cn("text-sm font-bold tabular-nums", tone.text)}>{cov}%</span>
              </div>
              <p className="text-meta text-ink-secondary mt-0.5">
                {f.controlsMet ?? "—"} controls met · {f.controlsTotal ?? "—"} mapped
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-inset overflow-hidden">
                <div className={cn("h-full transition-all duration-500 ease-out", tone.bar)} style={{ width: `${cov}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </PanelCard>
  );
}

function postureTone(cov) {
  if (cov >= 85) return { text: "text-risk-low",  bar: "bg-risk-low" };
  if (cov >= 70) return { text: "text-gold-700",  bar: "bg-gold-600" };
  return                    { text: "text-risk-high", bar: "bg-risk-high" };
}

function RecentMaterialEventsCard({ entries }) {
  return (
    <PanelCard
      title="Recent material events · last 12"
      action={
        <Link
          to="/settings/audit"
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-800 inline-flex items-center gap-0.5"
          data-testid="auditor-open-events"
        >
          View full log <ChevronRight size={11} />
        </Link>
      }
      testId="auditor-events-card"
    >
      {entries.length === 0 ? (
        <p className="text-meta text-ink-secondary">No audit entries yet.</p>
      ) : (
        <ol className="border-l border-border-subtle ml-2 space-y-2.5 pl-4">
          {entries.map((e, i) => (
            <li
              key={`${e.id || e.hash || i}`}
              className="relative"
              data-testid={`auditor-event-${i}`}
            >
              <span className={cn(
                "absolute -left-[21px] top-1 h-2 w-2 rounded-full ring-2 ring-raised",
                actionTone(e.action).dot,
              )} />
              <p className="text-sm text-ink-primary leading-snug">
                <span className="font-mono text-meta text-ink-disabled">{(e.ts || "").slice(0, 19).replace("T", " ")}</span>
                {" · "}
                <span className="font-semibold">{e.actor || "—"}</span>
                {" · "}
                <span className={cn("font-mono text-[11px]", actionTone(e.action).text)}>{e.action}</span>
              </p>
              <p className="text-meta text-ink-secondary mt-0.5">{e.narrative || "—"}</p>
            </li>
          ))}
        </ol>
      )}
    </PanelCard>
  );
}

function actionTone(action = "") {
  if (action.startsWith("deployment.reject"))    return { dot: "bg-risk-high",       text: "text-risk-high" };
  if (action.startsWith("incident") || action.includes("violation")) return { dot: "bg-risk-high", text: "text-risk-high" };
  if (action.startsWith("drift"))                return { dot: "bg-gold-600",       text: "text-gold-700" };
  if (action.startsWith("policy"))               return { dot: "bg-tralenex-600",   text: "text-tralenex-700" };
  if (action.startsWith("agent.born"))           return { dot: "bg-risk-low",       text: "text-risk-low" };
  if (action.startsWith("deployment.approve"))   return { dot: "bg-risk-low",       text: "text-risk-low" };
  return                                                { dot: "bg-ink-disabled",   text: "text-ink-secondary" };
}

function SignedEvidencePackCard({ reports }) {
  const top = reports.slice(0, 6);
  return (
    <PanelCard
      title="Signed evidence packs"
      action={
        <Link
          to="/reports/library"
          className="text-meta font-semibold text-tralenex-700 hover:text-tralenex-800 inline-flex items-center gap-0.5"
          data-testid="auditor-open-reports"
        >
          Library <ChevronRight size={11} />
        </Link>
      }
      testId="auditor-evidence-card"
    >
      {top.length === 0 ? (
        <p className="text-meta text-ink-secondary">No reports archived yet.</p>
      ) : (
        <ul className="space-y-2">
          {top.map((r) => (
            <li
              key={r.id}
              className="surface-card-inner p-2.5 border border-border-subtle rounded-md flex items-start gap-2.5"
              data-testid={`auditor-evidence-${r.id}`}
            >
              <FileCheck2 size={14} className={r.signed ? "text-risk-low mt-0.5" : "text-ink-disabled mt-0.5"} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink-primary truncate">{r.name}</p>
                <p className="text-meta text-ink-secondary">
                  <span className="font-mono">{r.id}</span> · {r.framework || "—"} · {r.generatedAt || "—"}
                </p>
              </div>
              {r.signed && (
                <span className="text-[10px] font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded bg-risk-low/10 text-risk-low border border-risk-low/30 shrink-0">
                  Signed
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </PanelCard>
  );
}

function AuditorWorkflowCard({ chainLength, incidentsOpen, policyViolations, kpis }) {
  return (
    <PanelCard title="Auditor checklist" testId="auditor-checklist-card">
      <ul className="space-y-2 text-meta">
        <ChecklistItem
          label="Audit chain integrity"
          value={`${chainLength} entries · verified`}
          ok
        />
        <ChecklistItem
          label="Open incidents"
          value={`${incidentsOpen} open`}
          ok={incidentsOpen === 0}
        />
        <ChecklistItem
          label="Policy violations · 30d"
          value={`${policyViolations} flagged`}
          ok={policyViolations === 0}
        />
        <ChecklistItem
          label="Shadow AI surfaced"
          value={`${kpis?.shadowAi ?? 0} detected`}
          ok={(kpis?.shadowAi ?? 0) === 0}
        />
        <ChecklistItem
          label="Avg framework coverage"
          value={`${kpis?.avgComplianceCoverage ?? 0}%`}
          ok={(kpis?.avgComplianceCoverage ?? 0) >= 80}
        />
      </ul>
      <p className="text-meta text-ink-disabled mt-3">
        Click any framework, policy or report to drill into the underlying evidence — every artefact is read-only at this seat.
      </p>
    </PanelCard>
  );
}

function ChecklistItem({ label, value, ok }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? (
        <CheckCircle2 size={13} className="text-risk-low shrink-0" />
      ) : (
        <AlertTriangle size={13} className="text-risk-high shrink-0" />
      )}
      <span className="text-ink-primary">{label}</span>
      <span className="ml-auto text-ink-secondary tabular-nums">{value}</span>
    </li>
  );
}

function AuditorSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="auditor-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[120px] rounded-md surface-card animate-pulse bg-inset/20" />
      <div className="mt-4 h-[320px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function AuditorError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="auditor-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Assurance · Auditor View</p>
        <h1 className="text-h2 text-ink-primary mt-1">Could not load the auditor view</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}
